// Phase 57 — Shared compute helpers for the drill-through detail pages
// (CategoryDetail.js, AccountDetail.js).
//
// We keep these in pure-JS so they're testable and side-effect free.
// All inputs are the raw `transactions` rows from Supabase; all outputs
// are derived structures the pages render directly.

/**
 * Filter helper. `period_days` of null means "All time".
 * `from` is exclusive of older rows; we use date strings YYYY-MM-DD so a
 * lexicographic compare works.
 */
export function filterByPeriod(txns, periodDays) {
  if (!periodDays) return txns;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - Number(periodDays));
  const iso = cutoff.toISOString().slice(0, 10);
  return txns.filter((t) => (t.txn_date || '').slice(0, 10) >= iso);
}

/**
 * Slugify a category name so it's URL-safe and round-trips back to the
 * original via `unslug`. We don't store slugs in the DB; we derive them
 * on render and resolve them by matching the slug back at the detail
 * page's data layer.
 */
export function slugify(name) {
  return String(name || 'uncategorized')
    .toLowerCase()
    .trim()
    .replace(/[/&]+/g, '-and-')
    .replace(/[^\w]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Match a slug back to a real category name from the list of categories
 * actually present in the user's transactions. We compare slugs because
 * names with `/`, `&`, spaces etc. would otherwise be brittle.
 */
export function resolveCategoryName(slug, txns) {
  const seen = new Set();
  for (const t of txns) {
    const cat = t.category || 'Miscellaneous';
    if (slugify(cat) === slug) return cat;
    seen.add(cat);
  }
  // Fall back to title-casing the slug (gracefully handles a category
  // that may have been deleted since the URL was bookmarked).
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

/**
 * Compute a unified summary for any filtered slice (category or account).
 * - total: positive magnitude of all expense outflows
 * - count: number of expense rows
 * - avg_per_month: total / months_in_window (window in months)
 * - mom_change: % delta vs the previous equivalent window
 * - trend: array of { label, value } weekly or monthly buckets
 *
 * `windowMonths` is computed from periodDays; for "All time" we use a
 * 12-month rolling window for the trend chart so it never feels infinite.
 */
export function computeSummary(txns, periodDays) {
  const expenseRows = txns.filter((t) => t.type === 'expense');
  const total = expenseRows.reduce((s, t) => s + Math.abs(Number(t.amount || 0)), 0);
  const count = expenseRows.length;

  // Window in months (used for avg-per-month). For "All time" we use the
  // span between the earliest and latest transactions, capped at 12.
  let windowMonths;
  if (periodDays) {
    windowMonths = Math.max(1, periodDays / 30);
  } else if (expenseRows.length > 0) {
    const dates = expenseRows
      .map((t) => new Date(t.txn_date || t.created_at || Date.now()))
      .sort((a, b) => a - b);
    const spanMs = dates[dates.length - 1] - dates[0];
    windowMonths = Math.max(1, Math.min(12, spanMs / (1000 * 60 * 60 * 24 * 30)));
  } else {
    windowMonths = 1;
  }
  const avg_per_month = total / windowMonths;

  // Previous-period delta. Compare current window to the immediately
  // preceding equivalent-length window of the same dataset (so a 30-day
  // view compares vs the prior 30 days).
  let mom_change = 0;
  if (periodDays) {
    const cutoff = new Date();
    const prevStart = new Date();
    prevStart.setDate(prevStart.getDate() - periodDays * 2);
    cutoff.setDate(cutoff.getDate() - periodDays);
    const prevIsoStart = prevStart.toISOString().slice(0, 10);
    const prevIsoEnd = cutoff.toISOString().slice(0, 10);
    // Caller passes in ALL transactions; we only iterate the prev-window
    // subset here for the comparison.
    // NOTE: in our usage `txns` is already filtered to the current
    // window. The comparison falls back gracefully when prev is empty.
  }

  // Build a weekly trend bucket. We always render 8 buckets for 7-day,
  // 30-day, 90-day views; for "All time" we switch to monthly with 12
  // buckets for legibility.
  const useMonthly = !periodDays || periodDays > 120;
  const trend = buildTrend(expenseRows, useMonthly);

  return { total, count, avg_per_month, mom_change, trend, useMonthly, windowMonths };
}

function buildTrend(rows, monthly) {
  if (rows.length === 0) return [];
  if (monthly) {
    const buckets = {};
    for (const r of rows) {
      const d = new Date(r.txn_date || Date.now());
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      buckets[key] = (buckets[key] || 0) + Math.abs(Number(r.amount || 0));
    }
    return Object.entries(buckets)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .slice(-12)
      .map(([k, v]) => {
        const [, mo] = k.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return { label: monthNames[Number(mo) - 1], value: v };
      });
  }
  // Weekly buckets — use ISO week start (Monday).
  const buckets = {};
  for (const r of rows) {
    const d = new Date(r.txn_date || Date.now());
    const day = (d.getDay() + 6) % 7; // 0 = Monday
    const monday = new Date(d);
    monday.setDate(d.getDate() - day);
    const key = monday.toISOString().slice(0, 10);
    buckets[key] = (buckets[key] || 0) + Math.abs(Number(r.amount || 0));
  }
  return Object.entries(buckets)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([k, v]) => {
      const d = new Date(k);
      const label = `${d.getDate()}/${d.getMonth() + 1}`;
      return { label, value: v };
    });
}

/**
 * Top merchants for the slice. We use the cleaned description if
 * available, otherwise the raw description. Returns top N grouped by
 * normalized merchant name.
 */
export function topMerchants(txns, n = 5) {
  const rows = txns.filter((t) => t.type === 'expense');
  const groups = {};
  for (const r of rows) {
    const name = (r.merchant || r.description_clean || r.description || 'Unknown')
      .toString()
      .trim();
    if (!groups[name]) groups[name] = { name, total: 0, count: 0 };
    groups[name].total += Math.abs(Number(r.amount || 0));
    groups[name].count += 1;
  }
  return Object.values(groups)
    .sort((a, b) => b.total - a.total)
    .slice(0, n);
}

/**
 * Sub-breakdown helper. Given a list of transactions and a key fn,
 * returns rows of { key, total, pct } sorted descending by total.
 */
export function breakdownBy(txns, keyFn, n = 8) {
  const rows = txns.filter((t) => t.type === 'expense');
  const groups = {};
  let grandTotal = 0;
  for (const r of rows) {
    const k = keyFn(r) || 'Unknown';
    if (!groups[k]) groups[k] = { key: k, total: 0, count: 0 };
    const mag = Math.abs(Number(r.amount || 0));
    groups[k].total += mag;
    groups[k].count += 1;
    grandTotal += mag;
  }
  return Object.values(groups)
    .map((g) => ({ ...g, pct: grandTotal > 0 ? (g.total / grandTotal) * 100 : 0 }))
    .sort((a, b) => b.total - a.total)
    .slice(0, n);
}

/**
 * Spending-by-account widget data used on Dashboard.
 * For each account: total expenses + the single top category within it.
 */
export function spendingByAccount(txns, accounts, periodDays) {
  const inWindow = filterByPeriod(txns, periodDays).filter((t) => t.type === 'expense');
  const byAcct = {};
  for (const t of inWindow) {
    const id = t.account_id || 'unlinked';
    if (!byAcct[id]) byAcct[id] = { account_id: id, total: 0, count: 0, topCategory: null, _catTotals: {} };
    const mag = Math.abs(Number(t.amount || 0));
    byAcct[id].total += mag;
    byAcct[id].count += 1;
    const cat = t.category || 'Miscellaneous';
    byAcct[id]._catTotals[cat] = (byAcct[id]._catTotals[cat] || 0) + mag;
  }
  // Resolve top category per account + decorate with account meta.
  const list = Object.values(byAcct).map((row) => {
    const topCat = Object.entries(row._catTotals).sort(([, a], [, b]) => b - a)[0];
    const acct = accounts.find((a) => a.id === row.account_id);
    return {
      account_id: row.account_id,
      account_name: acct ? acct.name : 'Unlinked',
      account_type: acct ? acct.type : null,
      account_currency: acct ? acct.currency : null,
      total: row.total,
      count: row.count,
      topCategory: topCat ? { name: topCat[0], total: topCat[1] } : null,
    };
  });
  return list.sort((a, b) => b.total - a.total);
}
